import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarDays,
  CheckCircle2,
  CreditCard,
  Layers,
  Bell,
  Users,
  Building2,
  Globe,
  Smartphone,
  Monitor,
} from "lucide-react";
import { subscriptionInfoService } from "@/services/subscription-info.service";
import { ROLE_PERMISSIONS } from "@/constants/permissions";

const MODULE_LABELS: Record<string, string> = {
  DASHBOARD: "Dashboard",
  SALES_MGMT: "Sales Management",
  INVENTORY: "Inventory",
  CUSTOMER_MGMT: "Customer Management",
  REPORTS: "Reports",
  USER_MGMT: "User Management",
  SETTINGS: "Settings",
  PROCUREMENT: "Procurement",
  SUPPLIER_MGMT: "Supplier Management",
  MULTI_STORE: "Multi-Store",
  LOYALTY_PROGRAM: "Loyalty Program",
  DISCOUNT: "Discount Schemes",
  DOCTOR_MGMT: "Doctor Management",
  HOSPITAL_MGMT: "Hospital Management",
  DRUG_GENERICS: "Drug Generics",
  COMPLIANCE: "Compliance",
  DRUG_LICENSE: "Drug Licenses",
  DESKTOP_APP: "Desktop Application",
  MOBILE_APP: "Mobile Application",
  OFFLINE_MODE: "Offline Mode",
  API_ACCESS: "API Access",
  WHITE_LABEL: "White Label",
  PRIORITY_SUPPORT: "Priority Support",
};

const ROLE_LABELS: Record<string, string> = {
  BUSINESS_OWNER: "Business Owner",
  STORE_MANAGER: "Store Manager",
  PHARMACIST: "Pharmacist",
};

const PERMISSION_GROUP_LABELS: Record<string, string> = {
  users: "Users",
  products: "Products",
  inventory: "Inventory",
  billing: "Billing",
  customers: "Customers",
  reports: "Reports",
  settings: "Settings",
  compliance: "Compliance",
};

const ROLE_ORDER = ["BUSINESS_OWNER", "STORE_MANAGER", "PHARMACIST"];

const formatDate = (value?: string | Date | null) => {
  if (!value) return "—";
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatLimit = (value: number) => {
  if (value < 0) return "Unlimited";
  return value.toString();
};

const formatModuleLabel = (code: string) =>
  MODULE_LABELS[code] ||
  code
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const summarizePermissions = (permissions: string[]) => {
  const groups = new Map<string, number>();
  permissions.forEach((permission) => {
    const [group] = permission.split(":");
    groups.set(group, (groups.get(group) || 0) + 1);
  });
  return Array.from(groups.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([group, count]) => ({
      label: PERMISSION_GROUP_LABELS[group] || group,
      count,
    }));
};

export default function SubscriptionOverviewPage() {
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ["subscription-summary"],
    queryFn: () => subscriptionInfoService.getSummary(),
  });

  const [upgradeAlerts, setUpgradeAlerts] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("planUpgradeAlerts");
    if (stored) {
      setUpgradeAlerts(stored === "true");
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("planUpgradeAlerts", String(upgradeAlerts));
  }, [upgradeAlerts]);

  const enabledModules = summary?.enabledModules ?? [];
  const moduleBadges = useMemo(
    () =>
      enabledModules.map((moduleCode) => ({
        code: moduleCode,
        label: formatModuleLabel(moduleCode),
      })),
    [enabledModules],
  );

  const latestPaymentDate = summary?.billing?.latestPaymentDate || null;
  const latestInvoiceNumber = summary?.billing?.invoiceNumber || null;

  const applications = [
    {
      key: "DASHBOARD",
      name: "Web",
      icon: Globe,
    },
    {
      key: "MOBILE_APP",
      name: "Mobile",
      icon: Smartphone,
    },
    {
      key: "DESKTOP_APP",
      name: "Desktop",
      icon: Monitor,
    },
  ].map((app) => ({
    ...app,
    enabled: enabledModules.includes(app.key),
  }));

  const roleSummaries = useMemo(() => {
    return ROLE_ORDER.map((role) => {
      const permissions = ROLE_PERMISSIONS[role] || [];
      return {
        role,
        label: ROLE_LABELS[role] || role,
        count: permissions.length,
        groups: summarizePermissions(permissions),
      };
    });
  }, []);

  const statusLabel = summary?.subscription.status || "ACTIVE";
  const statusClass =
    statusLabel === "ACTIVE"
      ? "bg-emerald-50 text-emerald-600"
      : statusLabel === "TRIAL"
        ? "bg-amber-50 text-amber-600"
        : "bg-slate-100 text-slate-600";

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-slate-900">
          Subscription Overview
        </h1>
        <p className="text-sm text-slate-500">
          Track your plan, limits, modules, and billing insights in one place.
        </p>
      </div>

      {summaryLoading && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Loading subscription details...
        </div>
      )}

      {!summaryLoading && summary && (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-400">
                    Current Plan
                  </p>
                  <h2 className="text-xl font-semibold text-slate-900">
                    {summary.subscription.planName} ·{" "}
                    {summary.subscription.status}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {summary.subscription.planType} plan
                  </p>
                </div>
                <div
                  className={`flex items-center gap-2 rounded-xl px-3 py-2 ${statusClass}`}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-widest">
                    {statusLabel}
                  </span>
                </div>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                  <div className="flex items-center gap-2 text-slate-500">
                    <CalendarDays className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-widest">
                      Plan Start
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-900">
                    {formatDate(summary.subscription.startDate)}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                  <div className="flex items-center gap-2 text-slate-500">
                    <CalendarDays className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-widest">
                      Plan End / Renewal
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-900">
                    {formatDate(
                      summary.subscription.endDate ||
                        summary.subscription.renewalDate,
                    )}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                  <div className="flex items-center gap-2 text-slate-500">
                    <CreditCard className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-widest">
                      Latest Payment Date
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-900">
                    {formatDate(latestPaymentDate)}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                  <div className="flex items-center gap-2 text-slate-500">
                    <CreditCard className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-widest">
                      Invoice Number
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-900">
                    {latestInvoiceNumber || "Not generated"}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-semibold text-slate-700">
                  Plan Features
                </h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(summary.subscription.features || []).map((feature) => (
                    <span
                      key={feature}
                      className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-semibold text-slate-700">
                  Enabled Modules
                </h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {moduleBadges.map((module) => (
                    <span
                      key={module.code}
                      className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-600"
                    >
                      {module.label}
                    </span>
                  ))}
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-700">
                  Preloaded Roles & Permission Summary
                </h3>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                {roleSummaries.map((role) => (
                  <div
                    key={role.role}
                    className="rounded-xl border border-slate-100 bg-slate-50/60 p-4"
                  >
                    <p className="text-sm font-semibold text-slate-800">
                      {role.label}
                    </p>
                    <p className="text-xs text-slate-500">
                      {role.count} permissions
                    </p>
                    <div className="mt-3 space-y-1">
                      {role.groups.map((group) => (
                        <div
                          key={group.label}
                          className="flex items-center justify-between text-xs text-slate-600"
                        >
                          <span>{group.label}</span>
                          <span className="font-semibold">{group.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="space-y-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <Layers className="h-5 w-5" />
                <h3 className="text-sm font-semibold">Plan Limits</h3>
              </div>
              <div className="mt-4 space-y-4">
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Building2 className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-widest">
                      Stores
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-900">
                    {summary.limits.currentStores} /{" "}
                    {formatLimit(summary.limits.maxStores)}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Users className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-widest">
                      Users
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-900">
                    {summary.limits.currentUsers} /{" "}
                    {formatLimit(summary.limits.maxUsers)}
                  </p>
                </div>
                {typeof summary.limits.storageGB === "number" && (
                  <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                    <div className="flex items-center gap-2 text-slate-500">
                      <Layers className="h-4 w-4" />
                      <span className="text-xs uppercase tracking-widest">
                        Storage (GB)
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-semibold text-slate-900">
                      {summary.limits.usedStorageGB ?? 0} /{" "}
                      {summary.limits.storageGB}
                    </p>
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <Globe className="h-5 w-5" />
                <h3 className="text-sm font-semibold">Applications Enabled</h3>
              </div>
              <div className="mt-4 space-y-3">
                {applications.map((app) => (
                  <div
                    key={app.key}
                    className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3"
                  >
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <app.icon className="h-4 w-4" />
                      {app.name}
                    </div>
                    <span
                      className={`text-xs font-semibold uppercase tracking-widest ${
                        app.enabled ? "text-emerald-600" : "text-slate-400"
                      }`}
                    >
                      {app.enabled ? "Enabled" : "Not in plan"}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <Bell className="h-5 w-5" />
                <h3 className="text-sm font-semibold">Plan Upgrade Alerts</h3>
              </div>
              <p className="mt-3 text-sm text-slate-500">
                Get notified when your usage is close to plan limits or when a
                better plan is available.
              </p>
              <div className="mt-4 flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    Upgrade notifications
                  </p>
                  <p className="text-xs text-slate-500">
                    Alerts appear in-app and email
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setUpgradeAlerts((prev) => !prev)}
                  className={`relative h-6 w-11 rounded-full transition-all ${
                    upgradeAlerts ? "bg-emerald-500" : "bg-slate-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                      upgradeAlerts ? "right-0.5" : "left-0.5"
                    }`}
                  />
                </button>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
